import { useId } from 'react'
import type { ReactNode } from 'react'
import { COLOR_GROUPS, COLOR_LABELS, MATERIALS, material, type BodyColor } from './buddy/materials'
import { Arms, JumpGround, LIMBS, LIMB_GROUPS, LIMB_LABELS, Legs, POSES, PoseBehind, armsFor, holdsSomething, type Limbs } from './buddy/poses'
import { BODY, INK, W } from './buddy/shared'

export { COLOR_GROUPS, LIMB_GROUPS, LIMB_LABELS, LIMBS, MATERIALS, POSES, armsFor, holdsSomething, material }
export type { BodyColor, Limbs }

/**
 * Sprout Buddy: the Word Sprout mascot, and the character a child can build.
 *
 * Drawn from Sam's mascot sheets (mascot/*.jpg, 8 Oct 2026): an orange
 * teardrop body with a dark outline, a simple face, and something growing
 * out of the top. The sheets are AI-made pictures, with garbled words on
 * them ("Vocabula Gardener", "component libary") and outlines that change
 * from one pose to the next, so they are a brief, not artwork to ship. This
 * is the same character redrawn as shapes: sharp at any size, a few
 * kilobytes, recolourable, and able to move.
 *
 * **Built from parts**, exactly the sheet's "component kit":
 *
 *   leaf    what grows on top: the classic two leaves, oak, clover,
 *           fiddlehead, palm, fern, maple, herbs or succulent
 *   color   the body: plain colours, gems, metals, space, nature and fun
 *           ones (buddy/materials.tsx)
 *   limbs   arms and legs: plain, waving, gardener, and poses such as a
 *           victory fist or a superhero with a cape (buddy/poses.tsx)
 *   held    nothing, a watering can, a shovel (saved as `trowel`), a book or a pencil
 *
 * Every part is free from the first minute. Making parts something to earn
 * would turn the one thing a child makes for themselves into a reward to be
 * chased, which D-049 and themes.ts both rule out.
 *
 * A buddy is stored as a short code, `buddy:clover.mint.gardener.can`, in
 * the same place as the other picture choices (`learner.avatar_ref`).
 */

export const LEAVES = [
  'sprout', 'oak', 'clover', 'fiddlehead', 'palm', 'fern', 'maple', 'herbs', 'succulent',
  // The fun ones (Sam, 8 Oct 2026: "lightning, helicopter wings, etc").
  'lightning', 'propeller', 'star', 'sunflower', 'crown', 'antennae',
] as const
export const HELD = ['none', 'can', 'trowel', 'book', 'pencil'] as const

export type Leaf = typeof LEAVES[number]
export type Held = typeof HELD[number]
export type Mood = 'happy' | 'cheer' | 'wonder' | 'sleepy'

export interface Buddy { leaf: Leaf; color: BodyColor; limbs: Limbs; held: Held }

/** The brand's own Sprout: two leaves, tangerine, arms and legs. */
export const DEFAULT_BUDDY: Buddy = { leaf: 'sprout', color: 'tangerine', limbs: 'simple', held: 'none' }

export const LEAF_GROUPS: [string, Leaf[]][] = [
  ['Plants', ['sprout', 'oak', 'clover', 'fiddlehead', 'palm', 'fern', 'maple', 'herbs', 'succulent']],
  ['Fun', ['lightning', 'propeller', 'star', 'sunflower', 'crown', 'antennae']],
]

export const LABELS: Record<string, string> = {
  sprout: 'Two leaves', oak: 'Oak', clover: 'Clover', fiddlehead: 'Fiddlehead', palm: 'Palm',
  fern: 'Fern', maple: 'Maple', herbs: 'Herbs', succulent: 'Succulent',
  lightning: 'Lightning', propeller: 'Propeller', star: 'Star', sunflower: 'Sunflower',
  crown: 'Crown', antennae: 'Antennae',
  ...COLOR_LABELS,
  ...LIMB_LABELS,
  can: 'Watering can', trowel: 'Shovel', book: 'Book', pencil: 'Pencil',
}

export function buddyCode(b: Buddy): string {
  return `buddy:${b.leaf}.${b.color}.${b.limbs}.${b.held}`
}

/** Read a stored code. Anything unknown falls back to the default part. */
export function parseBuddy(code: string | null | undefined): Buddy | null {
  if (!code || !code.startsWith('buddy:')) return null
  const [leaf, color, limbs, held] = code.slice(6).split('.')
  return {
    leaf: (LEAVES as readonly string[]).includes(leaf ?? '') ? leaf as Leaf : DEFAULT_BUDDY.leaf,
    color: (color ?? '') in MATERIALS ? color as BodyColor : DEFAULT_BUDDY.color,
    limbs: (LIMBS as readonly string[]).includes(limbs ?? '') ? limbs as Limbs : DEFAULT_BUDDY.limbs,
    held: (HELD as readonly string[]).includes(held ?? '') ? held as Held : DEFAULT_BUDDY.held,
  }
}

export function randomBuddy(): Buddy {
  const pick = <T,>(xs: readonly T[]) => xs[Math.floor(Math.random() * xs.length)]!
  return {
    leaf: pick(LEAVES), color: pick(Object.keys(MATERIALS) as BodyColor[]),
    limbs: pick(LIMBS), held: pick(HELD),
  }
}

// ------------------------------------------------------------------ drawing

const LEAF = '#9BDB4D'
const LEAF_MID = '#6CB33F'
const LEAF_DARK = '#4E8A2A'

let styled = false
function ensureStyle(): void {
  if (styled || typeof document === 'undefined') return
  styled = true
  const el = document.createElement('style')
  el.textContent = `
    @keyframes ws-buddy-sway { 0%,100% { transform: rotate(-4deg) } 50% { transform: rotate(4deg) } }
    @keyframes ws-buddy-bob { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-2.5px) } }
    @keyframes ws-buddy-wave { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(-16deg) } }
    .ws-buddy-top { transform-origin: 60px 36px; animation: ws-buddy-sway 3.2s ease-in-out infinite; }
    .ws-buddy-body { animation: ws-buddy-bob 2.8s ease-in-out infinite; }
    .ws-buddy-wave { transform-origin: 28px 96px; animation: ws-buddy-wave 1.4s ease-in-out infinite; }
    @keyframes ws-buddy-spin { 0% { transform: scaleX(1) } 25% { transform: scaleX(0.15) } 50% { transform: scaleX(-1) } 75% { transform: scaleX(0.15) } 100% { transform: scaleX(1) } }
    .ws-buddy-spin { transform-origin: 60px 14px; animation: ws-buddy-spin 0.5s linear infinite; }
    @keyframes ws-buddy-twinkle { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }
    .ws-buddy-twinkle { animation: ws-buddy-twinkle 1.6s ease-in-out infinite; }
    @keyframes ws-buddy-pump { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(8deg) } }
    .ws-buddy-pump { transform-origin: 30px 96px; animation: ws-buddy-pump 0.9s ease-in-out infinite; }
    @keyframes ws-buddy-hop { 0%,100% { transform: translateY(0) } 45% { transform: translateY(-9px) } }
    .ws-buddy-hop { animation: ws-buddy-hop 0.8s ease-in-out infinite; }
    @keyframes ws-buddy-beckon { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(-35deg) } }
    .ws-buddy-beckon { transform-origin: -2px 94px; animation: ws-buddy-beckon 0.7s ease-in-out infinite; }
    @keyframes ws-buddy-beat { 0%,100% { transform: scale(1) } 30% { transform: scale(1.18) } }
    .ws-buddy-beat { transform-origin: 60px 121px; animation: ws-buddy-beat 1s ease-in-out infinite; }
    @keyframes ws-buddy-pulse { 0%,100% { transform: scale(0.94) } 50% { transform: scale(1.04) } }
    .ws-buddy-pulse { transform-origin: 60px 94px; animation: ws-buddy-pulse 1.8s ease-in-out infinite; }
    @keyframes ws-buddy-flicker { 0%,100% { transform: scale(1, 1) } 50% { transform: scale(0.88, 1.14) } }
    .ws-buddy-flicker { transform-box: fill-box; transform-origin: 50% 100%; animation: ws-buddy-flicker 0.9s ease-in-out infinite; }
    @keyframes ws-buddy-orbit { to { stroke-dashoffset: -36 } }
    .ws-buddy-orbit { animation: ws-buddy-orbit 1.2s linear infinite; }
    .ws-buddy-orbit-back { animation: ws-buddy-orbit 2s linear infinite reverse; }
    @media (prefers-reduced-motion: reduce) {
      .ws-buddy-top, .ws-buddy-body, .ws-buddy-wave, .ws-buddy-spin, .ws-buddy-twinkle,
      .ws-buddy-pump, .ws-buddy-hop, .ws-buddy-beckon, .ws-buddy-beat, .ws-buddy-pulse,
      .ws-buddy-flicker, .ws-buddy-orbit, .ws-buddy-orbit-back { animation: none; }
    }
  `
  document.head.appendChild(el)
}

const mirror = (child: ReactNode, key: string) => (
  <g key={key} transform="translate(120 0) scale(-1 1)">{child}</g>
)

function Top({ leaf, animate = true }: { leaf: Leaf; animate?: boolean }) {
  const stem = <path d="M60 40 L60 30" stroke={INK} strokeWidth={6} strokeLinecap="round" />
  const stemIn = <path d="M60 40 L60 30" stroke={LEAF_MID} strokeWidth={3} strokeLinecap="round" />
  switch (leaf) {
    case 'sprout': {
      const one = (
        <g>
          <path d="M60 32 C50 12 30 10 18 18 C28 34 46 38 60 32 Z" fill={LEAF} stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M57 31 C46 26 34 21 24 19" stroke={LEAF_MID} strokeWidth={2} fill="none" strokeLinecap="round" />
        </g>
      )
      return <g>{stem}{stemIn}{one}{mirror(one, 'r')}</g>
    }
    case 'oak': {
      const one = (
        <g>
          <path d="M60 32 C57 25 52 22 47 21 C47 15 41 13 38 17 C35 11 28 12 28 18 C22 16 17 22 21 26
                   C16 30 20 37 26 35 C29 40 37 39 41 35 C47 38 55 36 60 32 Z"
            fill={LEAF} stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M58 31 L26 24 M44 28 L38 19 M40 27 L33 34" stroke={LEAF_MID} strokeWidth={1.8} fill="none" strokeLinecap="round" />
        </g>
      )
      return <g>{stem}{stemIn}{one}{mirror(one, 'r')}</g>
    }
    case 'clover': {
      const heart = 'M0 0 C-10 -6 -13 -15 -7 -18 C-3 -20 0 -17 0 -15 C0 -17 3 -20 7 -18 C13 -15 10 -6 0 0 Z'
      return (
        <g>
          <path d="M60 40 L60 20" stroke={INK} strokeWidth={6} strokeLinecap="round" />
          <path d="M60 40 L60 20" stroke={LEAF_MID} strokeWidth={3} strokeLinecap="round" />
          {[0, 90, 180, 270].map((a) => (
            <g key={a} transform={`translate(60 18) rotate(${a})`}>
              <path d={heart} fill={LEAF} stroke={INK} strokeWidth={W} strokeLinejoin="round" />
              <path d="M0 -2 L0 -13" stroke={LEAF_MID} strokeWidth={1.6} strokeLinecap="round" />
            </g>
          ))}
        </g>
      )
    }
    case 'fiddlehead': {
      const frond = (d: string, k: string) => (
        <g key={k}>
          <path d={d} stroke={INK} strokeWidth={7} fill="none" strokeLinecap="round" />
          <path d={d} stroke={LEAF} strokeWidth={4} fill="none" strokeLinecap="round" />
        </g>
      )
      const left = 'M58 36 C52 24 40 22 38 12 C36 4 46 0 49 6 C51 11 46 14 44 10'
      const mid = 'M60 36 C60 26 64 16 62 8 C61 2 54 2 54 7 C54 11 58 12 59 9'
      return <g>{frond(left, 'l')}{mirror(frond(left, 'l2'), 'r')}{frond(mid, 'm')}</g>
    }
    case 'palm': {
      const blade = 'M0 0 C5 -10 5 -26 0 -36 C-5 -26 -5 -10 0 0 Z'
      return (
        <g>
          {[-72, -48, -24, 0, 24, 48, 72].map((a, i) => (
            <g key={a} transform={`translate(60 34) rotate(${a}) scale(${i % 2 ? 0.85 : 1})`}>
              <path d={blade} fill={i % 2 ? LEAF_MID : LEAF} stroke={INK} strokeWidth={W - 0.5} strokeLinejoin="round" />
              <path d="M0 -3 L0 -30" stroke={LEAF_DARK} strokeWidth={1.4} />
            </g>
          ))}
        </g>
      )
    }
    case 'fern': {
      const frond = (side: number, k: string) => (
        <g key={k} transform={`translate(60 34) rotate(${side * 28})`}>
          <path d="M0 0 C0 -14 0 -28 0 -40" stroke={INK} strokeWidth={4} strokeLinecap="round" fill="none" />
          {Array.from({ length: 7 }, (_, i) => {
            const y = -6 - i * 5
            const len = 10 - i * 1.1
            return (
              <g key={i}>
                <ellipse cx={-len / 2 - 1} cy={y} rx={len / 2 + 1} ry={2.6} transform={`rotate(-20 ${-len / 2 - 1} ${y})`}
                  fill={LEAF_MID} stroke={INK} strokeWidth={1.4} />
                <ellipse cx={len / 2 + 1} cy={y} rx={len / 2 + 1} ry={2.6} transform={`rotate(20 ${len / 2 + 1} ${y})`}
                  fill={LEAF_MID} stroke={INK} strokeWidth={1.4} />
              </g>
            )
          })}
        </g>
      )
      return <g>{frond(-1, 'l')}{frond(1, 'r')}</g>
    }
    case 'maple': {
      return (
        <g>
          {stem}
          <path d="M60 36 L60 31" stroke="#A0401A" strokeWidth={3} strokeLinecap="round" />
          <path d="M60 31 L52 30 L54 26 L42 22 L46 19 L40 10 L49 12 L51 7 L57 13 L56 0 L60 -4 L64 0 L63 13
                   L69 7 L71 12 L80 10 L74 19 L78 22 L66 26 L68 30 Z"
            fill="#EE6A2C" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M60 30 L60 2 M60 22 L46 14 M60 22 L74 14" stroke="#B8461A" strokeWidth={1.6} fill="none" strokeLinecap="round" />
        </g>
      )
    }
    case 'herbs': {
      const sprig = (x: number, y: number, a: number, k: string) => (
        <g key={k} transform={`translate(${x} ${y}) rotate(${a})`}>
          <path d="M0 0 L0 -16" stroke={INK} strokeWidth={3.5} strokeLinecap="round" />
          <path d="M0 0 L0 -16" stroke={LEAF_DARK} strokeWidth={1.5} strokeLinecap="round" />
          {[[-5, -16], [5, -16], [0, -21], [-6, -9], [6, -9]].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={4.6} fill={i % 2 ? LEAF_MID : '#5FAE3A'} stroke={INK} strokeWidth={1.6} />
          ))}
        </g>
      )
      return <g>{sprig(60, 34, -38, 'a')}{sprig(60, 34, 0, 'b')}{sprig(60, 34, 38, 'c')}</g>
    }
    case 'lightning':
      return (
        <g>
          {stem}{stemIn}
          <path d="M64 33 L50 17 L58 16 L52 -2 L73 20 L64 21 L70 33 Z"
            fill="#FFD23F" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M58 12 L62 10" stroke="#FFF6C2" strokeWidth={2} strokeLinecap="round" />
        </g>
      )
    case 'propeller':
      return (
        <g>
          <path d="M60 38 L60 16" stroke={INK} strokeWidth={6} strokeLinecap="round" />
          <path d="M60 38 L60 16" stroke="#C9D3DC" strokeWidth={3} strokeLinecap="round" />
          <g className={animate ? 'ws-buddy-spin' : undefined}>
            <ellipse cx={42} cy={14} rx={18} ry={4.5} fill="#E2557A" stroke={INK} strokeWidth={2.4} />
            <ellipse cx={78} cy={14} rx={18} ry={4.5} fill="#5AB4E5" stroke={INK} strokeWidth={2.4} />
          </g>
          <circle cx={60} cy={14} r={5} fill="#F6C343" stroke={INK} strokeWidth={2.4} />
        </g>
      )
    case 'star': {
      const pts = Array.from({ length: 10 }, (_, i) => {
        const r = i % 2 ? 7 : 16
        const a = -Math.PI / 2 + (i * Math.PI) / 5
        return `${60 + r * Math.cos(a)},${14 + r * Math.sin(a)}`
      }).join(' ')
      return (
        <g>
          <path d="M60 40 L60 26" stroke={INK} strokeWidth={6} strokeLinecap="round" />
          <path d="M60 40 L60 26" stroke={LEAF_MID} strokeWidth={3} strokeLinecap="round" />
          <polygon points={pts} fill="#FFD23F" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <circle cx={56} cy={11} r={2} fill="#FFF6C2" />
        </g>
      )
    }
    case 'sunflower':
      return (
        <g>
          <path d="M60 40 L60 26" stroke={INK} strokeWidth={6} strokeLinecap="round" />
          <path d="M60 40 L60 26" stroke={LEAF_MID} strokeWidth={3} strokeLinecap="round" />
          {Array.from({ length: 12 }, (_, i) => (
            <ellipse key={i} cx={60} cy={1} rx={4.6} ry={9} fill="#FFC21A" stroke={INK} strokeWidth={2}
              transform={`rotate(${i * 30} 60 14)`} />
          ))}
          <circle cx={60} cy={14} r={8.5} fill="#7A4A22" stroke={INK} strokeWidth={2.4} />
          <circle cx={57} cy={12} r={1.4} fill="#A86B3A" />
          <circle cx={62} cy={16} r={1.4} fill="#A86B3A" />
        </g>
      )
    case 'crown':
      return (
        <g>
          <path d="M47 41 L45 22 L53 30 L60 17 L67 30 L75 22 L73 41 Z"
            fill="#F2C230" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M47 36 L73 36" stroke="#B8860B" strokeWidth={2} />
          <circle cx={60} cy={29} r={3} fill="#E2557A" stroke={INK} strokeWidth={1.6} />
          <circle cx={45} cy={21} r={2.4} fill="#FFF6C2" stroke={INK} strokeWidth={1.4} />
          <circle cx={75} cy={21} r={2.4} fill="#FFF6C2" stroke={INK} strokeWidth={1.4} />
          <circle cx={60} cy={16} r={2.4} fill="#FFF6C2" stroke={INK} strokeWidth={1.4} />
        </g>
      )
    case 'antennae':
      return (
        <g fill="none" strokeLinecap="round">
          <path d="M56 38 C52 26 44 22 40 12" stroke={INK} strokeWidth={5} />
          <path d="M56 38 C52 26 44 22 40 12" stroke="#6CB33F" strokeWidth={2.4} />
          <path d="M64 38 C68 26 76 22 80 12" stroke={INK} strokeWidth={5} />
          <path d="M64 38 C68 26 76 22 80 12" stroke="#6CB33F" strokeWidth={2.4} />
          <circle cx={40} cy={10} r={6} fill="#F48FB1" stroke={INK} strokeWidth={2.4} />
          <circle cx={80} cy={10} r={6} fill="#9EE3F5" stroke={INK} strokeWidth={2.4} />
          <circle cx={38} cy={8} r={1.6} fill="#fff" stroke="none" />
          <circle cx={78} cy={8} r={1.6} fill="#fff" stroke="none" />
        </g>
      )
    case 'succulent': {
      const petal = 'M0 0 C6 -6 6 -18 0 -24 C-6 -18 -6 -6 0 0 Z'
      return (
        <g>
          {Array.from({ length: 9 }, (_, i) => (
            <g key={`o${i}`} transform={`translate(60 26) rotate(${-120 + i * 30})`}>
              <path d={petal} fill="#8FD3B6" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />
            </g>
          ))}
          {Array.from({ length: 6 }, (_, i) => (
            <g key={`i${i}`} transform={`translate(60 26) rotate(${-100 + i * 40}) scale(0.62)`}>
              <path d={petal} fill="#B8E6D0" stroke={INK} strokeWidth={3} strokeLinejoin="round" />
            </g>
          ))}
          <circle cx={60} cy={26} r={3} fill="#E9F7EF" stroke={INK} strokeWidth={1.6} />
        </g>
      )
    }
  }
}

// `ink` is the face colour: dark, except on a dark body (galaxy, black
// hole), where dark eyes would disappear.
function Face({ mood, ink = INK }: { mood: Mood; ink?: string }) {
  const eyes = mood === 'sleepy' ? (
    <g stroke={ink} strokeWidth={3} strokeLinecap="round" fill="none">
      <path d="M43 92 Q48 96 53 92" />
      <path d="M67 92 Q72 96 77 92" />
    </g>
  ) : (
    <g>
      <ellipse cx={48} cy={91} rx={mood === 'wonder' ? 5 : 4.4} ry={mood === 'wonder' ? 6.2 : 5.4} fill={ink} />
      <ellipse cx={72} cy={91} rx={mood === 'wonder' ? 5 : 4.4} ry={mood === 'wonder' ? 6.2 : 5.4} fill={ink} />
      <circle cx={49.6} cy={88.6} r={1.6} fill="#fff" />
      <circle cx={73.6} cy={88.6} r={1.6} fill="#fff" />
    </g>
  )
  const mouth =
    mood === 'cheer' ? (
      <g>
        <path d="M51 101 Q60 115 69 101 Z" fill="#6B2418" stroke={ink} strokeWidth={2.6} strokeLinejoin="round" />
        <path d="M55 107 Q60 111 65 107" fill="#F07A7A" />
      </g>
    ) : mood === 'wonder' ? (
      <ellipse cx={60} cy={106} rx={4} ry={5} fill="#6B2418" stroke={ink} strokeWidth={2.4} />
    ) : (
      <path d="M52 103 Q60 111 68 103" stroke={ink} strokeWidth={3} fill="none" strokeLinecap="round" />
    )
  return (
    <g>
      <ellipse cx={40} cy={103} rx={5.5} ry={3.4} fill="#FF7E8E" opacity={0.35} />
      <ellipse cx={80} cy={103} rx={5.5} ry={3.4} fill="#FF7E8E" opacity={0.35} />
      {eyes}
      {mouth}
    </g>
  )
}

function Held({ held }: { held: Held }) {
  switch (held) {
    case 'can':
      return (
        <g>
          <path d="M98 104 L120 92" stroke={INK} strokeWidth={6} strokeLinecap="round" />
          <path d="M98 104 L120 92" stroke="#3E8E6A" strokeWidth={3} strokeLinecap="round" />
          <path d="M117 88 L125 92 L121 99 Z" fill="#3E8E6A" stroke={INK} strokeWidth={2} strokeLinejoin="round" />
          <path d="M86 98 C86 94 106 94 106 98 L104 120 C104 124 88 124 88 120 Z" fill="#4CA77E" stroke={INK} strokeWidth={W} strokeLinejoin="round" />
          <path d="M90 98 C88 86 104 86 102 98" stroke={INK} strokeWidth={3} fill="none" />
          <path d="M92 110 C95 105 99 105 100 110 C99 114 95 115 92 110 Z" fill="#B6E3C8" />
          <g fill="#7CC4F0">
            <ellipse cx={126} cy={104} rx={1.6} ry={2.6} />
            <ellipse cx={122} cy={110} rx={1.6} ry={2.6} />
            <ellipse cx={128} cy={114} rx={1.6} ry={2.6} />
          </g>
        </g>
      )
    // Saved as `trowel`, drawn as a shovel (Sam, 11 Oct: the short handle and
    // pointed blade "look like a knife"). A long handle with a T-grip, held
    // upright, and a wide blade at the bottom, which no knife has. The key
    // stays `trowel` so buddies already saved keep what they hold.
    case 'trowel':
      return (
        <g transform="translate(100 113) rotate(-12)">
          <rect x={-2.4} y={-30} width={4.8} height={46} rx={2} fill="#9B5B2E" stroke={INK} strokeWidth={2.2} />
          <rect x={-8} y={-35} width={16} height={6} rx={3} fill="#9B5B2E" stroke={INK} strokeWidth={2.2} />
          <path d="M-3.5 13 L3.5 13 L5 19 L-5 19 Z" fill="#8E99A4" stroke={INK} strokeWidth={2} strokeLinejoin="round" />
          <path d="M-10.5 18 L10.5 18 L10.5 30 C10.5 37 5 41 0 43.5 C-5 41 -10.5 37 -10.5 30 Z"
            fill="#C9D3DC" stroke={INK} strokeWidth={2.4} strokeLinejoin="round" />
          <path d="M-7.5 22 L7.5 22" stroke="#FFFFFF" strokeWidth={1.6} strokeLinecap="round" opacity={0.8} />
        </g>
      )
    case 'book':
      return (
        <g transform="translate(102 112) rotate(-8)">
          <path d="M-14 -10 L0 -6 L14 -10 L14 10 L0 14 L-14 10 Z" fill="#4C7BE0" stroke={INK} strokeWidth={2.6} strokeLinejoin="round" />
          <path d="M-12 -8 L0 -4 L12 -8 L12 8 L0 12 L-12 8 Z" fill="#FFF7E6" />
          <path d="M0 -4 L0 12" stroke={INK} strokeWidth={1.8} />
          <path d="M-9 -2 L-3 0 M-9 2 L-3 4 M3 0 L9 -2 M3 4 L9 2" stroke="#8A94A0" strokeWidth={1.2} strokeLinecap="round" />
        </g>
      )
    case 'pencil':
      return (
        <g transform="translate(101 112) rotate(-30)">
          <rect x={-3.5} y={-22} width={7} height={28} fill="#F6C343" stroke={INK} strokeWidth={2.2} />
          <path d="M-3.5 -22 L0 -31 L3.5 -22 Z" fill="#F3D9B0" stroke={INK} strokeWidth={2} strokeLinejoin="round" />
          <path d="M-1.3 -27 L0 -31 L1.3 -27 Z" fill={INK} />
          <rect x={-3.5} y={6} width={7} height={5} rx={1.5} fill="#F28C9B" stroke={INK} strokeWidth={2} />
        </g>
      )
    default:
      return null
  }
}

export function SproutBuddy({
  buddy = DEFAULT_BUDDY, mood = 'happy', size = 96, animate = true, label = 'Sprout Buddy',
}: {
  buddy?: Buddy
  mood?: Mood
  size?: number
  animate?: boolean
  label?: string
}) {
  ensureStyle()
  // Gradients need an id unique to this one drawing: there are often many
  // buddies on one screen (the buddy maker shows a whole grid at once).
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const m = material(buddy.color)
  const fill = m.stops ? `url(#b${uid})` : m.limbs
  // Cheering shows arms up, so the big moments look big. A pose is already
  // big, so it stays.
  const limbs: Limbs = mood === 'cheer' && (buddy.limbs === 'simple' || buddy.limbs === 'gardener') ? 'wave' : buddy.limbs
  const pose = POSES[limbs] ?? {}
  const figure = (
    <g className={animate ? 'ws-buddy-body' : undefined}>
      {m.behind?.(uid)}
      <PoseBehind limbs={limbs} />
      <Legs limbs={limbs} fill={m.limbs} />
      <g className={animate ? 'ws-buddy-top' : undefined}><Top leaf={buddy.leaf} animate={animate} /></g>
      {/* the teardrop body */}
      <path d={BODY} fill={fill} stroke={INK} strokeWidth={W + 0.4} strokeLinejoin="round" />
      {m.finish ? <g clipPath={`url(#c${uid})`}>{m.finish(uid)}</g> : null}
      <path d="M80 64 C92 78 98 96 94 112 C90 126 78 136 62 138 C78 128 87 112 87 96 C87 84 85 74 80 64 Z"
        fill={m.shade} opacity={m.stops ? 0.35 : 0.75} />
      <ellipse cx={50} cy={56} rx={2.6} ry={2} fill="#fff" opacity={0.45} />
      <ellipse cx={56} cy={52} rx={1.8} ry={1.4} fill="#fff" opacity={0.45} />
      {m.front?.(uid)}
      <Face mood={mood} ink={m.face ?? INK} />
      <Arms limbs={limbs} fill={m.limbs} held={buddy.held} />
      {holdsSomething(limbs) || limbs === 'none' ? <Held held={buddy.held} /> : null}
    </g>
  )
  return (
    <svg
      viewBox="-8 -8 140 164"
      width={size}
      height={Math.round((size * 164) / 140)}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {m.stops ? (
        <defs>
          {m.radial ? (
            <radialGradient id={`b${uid}`} cx="0.5" cy="0.58" r="0.62">
              {m.stops.map(([at, colour]) => <stop key={at} offset={at} stopColor={colour} />)}
            </radialGradient>
          ) : (
            <linearGradient id={`b${uid}`} x1="0" y1="0" x2={m.tilt ?? 0} y2="1">
              {m.stops.map(([at, colour]) => <stop key={at} offset={at} stopColor={colour} />)}
            </linearGradient>
          )}
          {m.defs?.(uid)}
          <clipPath id={`c${uid}`}><path d={BODY} /></clipPath>
        </defs>
      ) : null}
      {pose.lift ? (
        <>
          <JumpGround />
          <g transform="translate(0 -8)"><g className={animate ? 'ws-buddy-hop' : undefined}>{figure}</g></g>
        </>
      ) : figure}
    </svg>
  )
}
